import moment from 'moment';
import { useQuery } from 'react-query';
import { lovageApi } from 'src/axios';
import { fDate } from 'src/utils/formatTime';
import { trackCallStatisticsViewed } from '../../analytics';
const qs = require('qs');

export interface ChartData {
  labels: string[];
  values: number[];
}

export interface LocationSummaryGraph {
  total_calls: ChartData;
  texts_sent: ChartData;
  links_clicked: ChartData;
  connected_to_shop: ChartData;
}
/**
 * Location call statistics
 */
export interface LocationCallStatistics {
  /**
   * Total count of all calls Lovage processed
   */
  calls_processed: number;
  /**
   * Amount of time that Lovage saved the customer
   */
  time_saved: number;
  /**
   * Number of questions that were asked by customers
   */
  questions: number;
  /**
   * Percentage of calls that had a user open a booking link when they explicitly asked for one
   */
  text_conversion: number;
  /**
   * Graph data for the given date range
   */
  summary_graph: LocationSummaryGraph;
}

export interface Analytics {
  timeframe: string; // timeframe label
}

export interface CallStatisticsArgs {
  request: CallStatisticsRequest;
  analytics?: Analytics;
}

export interface CallStatisticsRequest {
  locationIds: number[] | undefined;
  startDate: Date;
  endDate: Date;
}

const validRequest = (request: CallStatisticsRequest): boolean =>
  (request.locationIds && request.locationIds.length > 0) ?? false;

export const fetchCallStatistics = (request: CallStatisticsRequest, analytics?: Analytics) => {
  console.log('fetchLocationCallStatistics');

  if (analytics && validRequest(request) && request.locationIds) {
    const totalDays = moment(request.endDate).diff(request.startDate, 'days');
    trackCallStatisticsViewed({
      locationIds: request.locationIds,
      startDate: request.startDate,
      endDate: request.endDate,
      timeframe: analytics.timeframe,
      totalDays,
    });
  }

  if (!validRequest(request)) {
    console.warn('No locationId provided to fetchCallStatistics - fetch aborted.');
    return;
  }

  return lovageApi
    .get<LocationCallStatistics>(`/dashboard/statistics`, {
      params: {
        location_id: request.locationIds,
        start_date: request.startDate,
        end_date: request.endDate,
      },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    })
    .then((res) => res.data);
};

export default function useCallStatistics({ request, analytics }: CallStatisticsArgs) {
  const { startDate, endDate, locationIds } = request;

  const queryKey = [
    'callStatistics',
    locationIds,
    { startDate: fDate(startDate), endDate: fDate(endDate) }, // fDate gets just the month, day, and year so React Query doesn't invalidate the cache for identical dates
  ];
  return useQuery([queryKey], () => fetchCallStatistics(request, analytics), {
    refetchOnWindowFocus: false,
  });
}
